import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  fetchActivity,
  subscribeWebPush,
  subscribeFcm,
  getVapidPublicKey,
} from '../utils/contentActivityApi';

const LAST_SEEN_KEY = 'mgv_content_activity_last_seen';
const POLL_MS = 60000;

export const NAV_ROLLUP = { studio: ['studio', 'playlist', 'collection'] };

export function expandSections(navKey) {
  return NAV_ROLLUP[navKey] || [navKey];
}

export function computeUnseen(items, lastSeen) {
  const perSection = {};
  let totalUnseen = 0;
  for (const item of items || []) {
    const seenAt = lastSeen ? lastSeen[item.section] : undefined;
    const isNew =
      !seenAt || new Date(item.createdAt).getTime() > new Date(seenAt).getTime();
    if (isNew) {
      perSection[item.section] = (perSection[item.section] || 0) + 1;
      totalUnseen += 1;
    }
  }
  return { perSection, totalUnseen };
}

export function unseenForNavKey(navKey, perSection) {
  const sections = expandSections(navKey);
  return sections.reduce((sum, s) => sum + (perSection ? perSection[s] || 0 : 0), 0);
}

export function markSeenInMap(map, navKey, stamp) {
  const next = { ...(map || {}) };
  for (const s of expandSections(navKey)) next[s] = stamp;
  return next;
}

function loadLastSeen() {
  try {
    const raw = localStorage.getItem(LAST_SEEN_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
}

function saveLastSeen(map) {
  try {
    localStorage.setItem(LAST_SEEN_KEY, JSON.stringify(map || {}));
  } catch (e) {
    /* ignore */
  }
}

const ContentActivityCtx = createContext(null);

export function ContentActivityProvider({ children, pollIntervalMs = POLL_MS }) {
  const [items, setItems] = useState([]);
  const [lastSeen, setLastSeen] = useState(() => loadLastSeen());
  const [perSection, setPerSection] = useState({});
  const [totalUnseen, setTotalUnseen] = useState(0);
  const [error, setError] = useState(null);
  const [pushStatus, setPushStatus] = useState('unknown');
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const recompute = useCallback((list, seen) => {
    const { perSection: ps, totalUnseen: t } = computeUnseen(list, seen);
    setPerSection(ps);
    setTotalUnseen(t);
  }, []);

  useEffect(() => {
    let active = true;
    const tick = async () => {
      try {
        const data = await fetchActivity();
        if (!active) return;
        const list = Array.isArray(data) ? data : (data && data.items ? data.items : []);
        setItems(list);
        recompute(list, loadLastSeen());
      } catch (e) {
        setError(e);
      }
    };
    tick();
    const id = setInterval(tick, pollIntervalMs);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [pollIntervalMs, recompute]);

  useEffect(() => {
    recompute(items, lastSeen);
  }, [items, lastSeen, recompute]);

  const markSeen = useCallback((navKey) => {
    const stamp = new Date().toISOString();
    const next = markSeenInMap(loadLastSeen(), navKey, stamp);
    saveLastSeen(next);
    setLastSeen(next);
  }, []);

  const markAllSeen = useCallback(() => {
    const stamp = new Date().toISOString();
    const next = { ...loadLastSeen() };
    for (const item of itemsRef.current) next[item.section] = stamp;
    saveLastSeen(next);
    setLastSeen(next);
  }, []);

  const enablePush = useCallback(async () => {
    try {
      if (
        typeof navigator === 'undefined' ||
        !('serviceWorker' in navigator) ||
        !('PushManager' in window) ||
        !('Notification' in window)
      ) {
        setPushStatus('unsupported');
        return false;
      }
      const permission = await Notification.requestPermission();
      setPushStatus(permission);
      if (permission !== 'granted') return false;
      await navigator.serviceWorker.register('/push-sw.js');
      await navigator.serviceWorker.ready;
      const key = await getVapidPublicKey();
      const sub = await navigator.serviceWorker.ready.then((reg) =>
        reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: key || undefined,
        })
      );
      const ok = await subscribeWebPush(sub.toJSON ? sub.toJSON() : sub);
      return ok;
    } catch (e) {
      setPushStatus('denied');
      return false;
    }
  }, []);

  const registerFcm = useCallback(async (token) => {
    if (!token) return false;
    return subscribeFcm(token);
  }, []);

  const value = {
    items,
    perSection,
    totalUnseen,
    lastSeen,
    error,
    pushStatus,
    markSeen,
    markAllSeen,
    enablePush,
    registerFcm,
  };

  return (
    <ContentActivityCtx.Provider value={value}>{children}</ContentActivityCtx.Provider>
  );
}

export function useContentActivity() {
  const ctx = useContext(ContentActivityCtx);
  if (!ctx) {
    throw new Error('useContentActivity must be used within a ContentActivityProvider');
  }
  return ctx;
}

export default ContentActivityProvider;
